// Frontend/src/component/facilities/Facilities.jsx
import React, { useEffect, useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import styles from "./Facilities.module.css";

import elearningIcon from "../../asset/e-learning-icon.png";
import supplyChainIcon from "../../asset/supply-chain-icon.png";
import healthcareIcon from "../../asset/hospital-patient-icon.png";
import chatbotIcon from "../../asset/communication.png";
import summarizationIcon from "../../asset/report.png";
import marketTrendIcon from "../../asset/data-analytics-icon.png";
import marketingIcon from "../../asset/marketing-icon.png";

const features = [
  { name: "E-learning Assistance", icon: elearningIcon, color: "#00d4ff", x: 20, y: 20 },
  // { name: "Supply Chain Management", icon: supplyChainIcon, color: "#9333ea", x: 50, y: 40 },
  { name: "Healthcare Scheduling", icon: healthcareIcon, color: "#00ff88", x: 20, y: 60 },
  { name: "Customer Support Chatbots", icon: chatbotIcon, color: "#ff6b35", x: 50, y: 80 },
  { name: "Document Summarization", icon: summarizationIcon, color: "#00d4ff", x: 20, y: 80 },
  { name: "Market Trend Analysis", icon: marketTrendIcon, color: "#9333ea", x: 50, y: 20 },
  { name: "Marketing Recommendations", icon: marketingIcon, color: "#00ff88", x: 20, y: 40 },
];

const Facilities = () => {
  const canvasRef = useRef(null);
  const connectionLinesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;

    const drawConnections = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw animated connection lines
      features.forEach((feature, i) => {
        const x1 = feature.x * canvas.width / 100;
        const y1 = feature.y * canvas.height / 100;
        
        // Connect to center AI brain
        const x2 = canvas.width / 2;
        const y2 = canvas.height / 2;
        
        // Animate the connection line
        const progress = (Math.sin(time * 0.001 + i) + 1) / 2;
        
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        
        // Gradient stroke based on feature color
        const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
        gradient.addColorStop(0, feature.color + "00");
        gradient.addColorStop(progress, feature.color + "ff");
        gradient.addColorStop(1, feature.color + "00");
        
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.5;
        ctx.globalAlpha = 0.6 + 0.4 * Math.sin(time * 0.002 + i);
        ctx.stroke();
        
        // Draw pulse along the line
        const pulseX = x1 + (x2 - x1) * progress;
        const pulseY = y1 + (y2 - y1) * progress;
        
        ctx.beginPath();
        ctx.arc(pulseX, pulseY, 3, 0, Math.PI * 2);
        ctx.fillStyle = feature.color;
        ctx.globalAlpha = 0.8;
        ctx.fill();
      });
      
      time += 16;
      animationId = requestAnimationFrame(drawConnections);
    };

    drawConnections();

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <section id="facilities" className={styles.section}>
      <Box className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <Typography className={styles.title}>
            AI MeBot Facilities
          </Typography>
          <Typography className={styles.subtitle}>
            Interconnected AI facilities powering intelligent solutions
          </Typography>
        </div>

        <Grid container spacing={6} alignItems="center">
          {/* LEFT - Feature Grid */}
          <Grid item xs={12} md={6}>
            <div className={styles.featureMatrix}>
              {features.map((feature, index) => (
                <div 
                  key={feature.name} 
                  className={styles.featureNode}
                  style={{ 
                    '--feature-color': feature.color,
                    '--node-x': `${feature.x}%`,
                    '--node-y': `${feature.y}%`
                  }}
                  data-tooltip={feature.name}
                >
                  <div className={styles.nodeContainer}>
                    <div className={styles.nodeRing}></div>
                    <div className={styles.nodeGlow}></div>
                    <div className={styles.nodeCore}>
                      <img
                        src={feature.icon}
                        alt={feature.name}
                        className={styles.nodeIcon}
                      />
                    </div>
                  </div>
                  <div className={styles.nodeLabel}>
                    {feature.name}
                  </div>
                </div>
              ))}
            </div>
          </Grid>

          {/* RIGHT - Neural Network Visualization */}
          <Grid item xs={12} md={6} className={styles.rightSection}>
            <div className={styles.neuralNetwork}>
              {/* Connection Canvas */}
              <canvas 
                ref={canvasRef} 
                className={styles.connectionCanvas}
                width={600}
                height={600}
              />
              
              {/* Center AI Brain */}
              <div className={styles.aiBrain}>
                <div className={styles.brainGlow}></div>
                <div className={styles.brainPulse}></div>
                <div className={styles.brainCore}>
                  {/* <img src={aiImage} alt="AI Brain" className={styles.brainImage} /> */}
                  <div className={styles.brainText}>
                    <span className={styles.brainTitle}>AI</span>
                    <span className={styles.brainSubtitle}>CORE</span>
                  </div>
                </div>
              </div>

              {/* Feature Connection Points */}
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={styles.connectionPoint}
                  style={{ 
                    left: `${feature.x}%`,
                    top: `${feature.y}%`,
                    '--point-color': feature.color
                  }}
                >
                  <div className={styles.pointGlow}></div>
                  <div className={styles.pointCore}></div>
                </div>
              ))}
            </div>
          </Grid>
        </Grid>

        {/* Tech Stats Bar */}
        {/* <div className={styles.statsBar}>
          <div className={styles.stat}>
            <div className={styles.statValue}>99.8%</div>
            <div className={styles.statLabel}>Accuracy</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>24/7</div>
            <div className={styles.statLabel}>Uptime</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>∞</div>
            <div className={styles.statLabel}>Scalability</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>50ms</div>
            <div className={styles.statLabel}>Response</div>
          </div>
        </div> */}
      </Box>
    </section>
  );
};

export default Facilities;